/**
 * Landing — the screen in front of the game, and the whole of the invite flow.
 *
 * Three ways in: alone, into somebody else's bay, or into one of your own that other people
 * can be sent to. That third one is the only reason this file is more than three buttons,
 * because "shareable link" is a promise about something that has to survive being pasted into
 * a chat window and clicked by a stranger a day later.
 *
 * WHAT MAKES THE LINK WORK is in shared/protocol.js, not here: the bay code determines the
 * reef, so a link carries everything needed to be in the same ocean as whoever sent it. This
 * file only has to put a code in the URL and read it back out.
 *
 * ARRIVING ON A LINK IS THE IMPORTANT PATH and it gets special treatment: the landing still
 * appears (you cannot drop someone into a live multiplayer session without a click — the
 * browser will not start audio without a gesture, and neither should the game), but it comes
 * up already pointed at the bay in the URL, with one obvious button.
 *
 * THE RELAY MAY NOT EXIST. With no VITE_RELAY_URL there is no multiplayer at all, and rather
 * than offer buttons that cannot work, the two bay options say so and only SOLO is live.
 */
import { formatBay, normaliseBay } from '../../shared/protocol.js';
import { listBays, makeBayCode, type BayListing } from '../net/NetClient.ts';
import { defaultSpec, speciesList, type SharkSpec } from '../voxel/SharkForge.ts';

export interface Session {
  /** '' means solo: no socket is ever opened */
  bay: string;
  /** true only for a bay this player created; it is what makes it public */
  listed: boolean;
  /** the animal the player built */
  spec: SharkSpec;
}

/** what a player last built, so a returning visitor is not made to do it again */
const SHARK_KEY = 'shoreplay.shark.v1';

function loadSpec(): SharkSpec {
  const base = defaultSpec((Math.random() * 0xffffffff) >>> 0);
  try {
    const raw = localStorage.getItem(SHARK_KEY);
    if (raw === null) return base;
    const p = JSON.parse(raw) as Partial<SharkSpec>;
    return {
      species: typeof p.species === 'string' ? p.species : base.species,
      seed: typeof p.seed === 'number' ? p.seed >>> 0 : base.seed,
      // Clamped rather than trusted: this is the only place a stored value reaches the
      // generator, and a size of 1e9 out of hand-edited storage is a browser tab that dies.
      size: clamp(typeof p.size === 'number' ? p.size : 1, 0.6, 1.4),
      age: clamp(typeof p.age === 'number' ? p.age : 0.2, 0, 1),
      name: typeof p.name === 'string' ? p.name.slice(0, NAME_MAX) : '',
    };
  } catch {
    return base;
  }
}

function saveSpec(spec: SharkSpec): void {
  try {
    localStorage.setItem(SHARK_KEY, JSON.stringify(spec));
  } catch {
    /* blocked storage — they just build it again next time */
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** a pointer that cannot hover — telling a phone to right-drag is telling it nothing */
function coarse(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

/** long enough for a real name, short enough that it cannot be used as a billboard */
export const NAME_MAX = 18;

/** how the bay code rides in the URL */
export const BAY_PARAM = 'bay';

/** read a bay code out of the current URL, '' if there is not one */
export function bayFromUrl(): string {
  return normaliseBay(new URLSearchParams(location.search).get(BAY_PARAM));
}

/**
 * The link that puts somebody else in this bay.
 *
 * Carries the code in its DASHED form, the same way it is shown on screen and the same way
 * anyone would say it out loud. normaliseBay strips punctuation, so it round-trips exactly;
 * the dash costs one character and makes the difference between a link someone can check
 * against a code they were told and one they have to squint at.
 */
export function inviteUrl(code: string): string {
  const url = new URL(location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set(BAY_PARAM, formatBay(code));
  return url.toString();
}

/** put the bay in the address bar without navigating, so a refresh stays in the same bay */
export function writeBayToUrl(code: string): void {
  const url = new URL(location.href);
  if (code) url.searchParams.set(BAY_PARAM, code);
  else url.searchParams.delete(BAY_PARAM);
  history.replaceState(null, '', url);
}

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

export class Landing {
  /** rebuild this shark. Set by the caller; the forge lives on the mesh worker. */
  onSpec: ((spec: SharkSpec) => void) | null = null;

  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly shark: HTMLDivElement;
  private readonly session: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  private readonly speciesRow: HTMLDivElement;
  private readonly nameEl: HTMLDivElement;
  private readonly latinEl: HTMLDivElement;
  private readonly dials: Array<() => void> = [];
  private readonly spec: SharkSpec;
  private specTimer: number | null = null;
  private readonly joinBox: HTMLDivElement;
  private readonly bayListEl: HTMLDivElement;
  private readonly joinRow: HTMLDivElement;
  private readonly codeInput: HTMLInputElement;
  private readonly liveEl: HTMLSpanElement;

  private resolve: ((s: Session) => void) | null = null;
  private pollTimer: number | null = null;
  private abort: AbortController | null = null;
  private bays: BayListing[] = [];
  private readonly online: boolean;

  constructor(parent: HTMLElement, relayConfigured: boolean) {
    this.online = relayConfigured;

    this.root = mk('div', 'land', parent);

    /* Rotate prompt. Shown by CSS on a coarse pointer in portrait and by nothing else — the
       chooser wants a wide frame, because the whole point of it is a shark beside the dials
       rather than behind them.

       A prompt and not a wall: it sits at the top and the panel underneath stays usable. A
       page that refuses to work until the phone is turned is a page that loses whoever was
       holding it one-handed on a train. */
    const rotate = mk('div', 'land-rotate', this.root);
    mk('div', 'land-rotate-icon', rotate);
    mk('div', 'land-rotate-text', rotate).textContent = 'TURN YOUR PHONE FOR THE FULL VIEW';

    this.panel = mk('div', 'land-panel', this.root);

    const head = mk('div', 'land-head', this.panel);
    mk('h1', 'land-title', head).textContent = 'SHOREPLAY';
    mk('p', 'land-sub', head).textContent = 'A REEF, A SHARK, AND NOWHERE TO BE';

    /* ---- step one: the animal --------------------------------------------------------
       Before anywhere to swim, something to swim as. Two steps rather than one long form:
       the whole panel would otherwise be taller than a laptop screen, and the two decisions
       are unrelated — which shark you are has nothing to do with who you swim with. */
    this.spec = loadSpec();
    this.shark = mk('div', 'land-shark', this.panel);

    /* ONE SCROLLING ROW, not a grid. Eight species in two columns is four rows of chrome
       before a single dial, which on a phone pushes everything that matters below the fold —
       and the roster is the least interesting decision on the panel, because seven of the eight
       are a tap away whichever is showing. */
    this.speciesRow = mk('div', 'land-species', this.shark);
    for (const sp of speciesList()) {
      const chip = mk('button', 'land-chip', this.speciesRow);
      chip.type = 'button';
      chip.dataset.key = sp.key;
      chip.textContent = sp.name.toUpperCase();
      chip.addEventListener('click', () => {
        this.spec.species = sp.key;
        this.markSpecies();
        this.readout();
        this.queueRebuild();
        chip.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      });
    }

    /* What you are actually looking at, named. The turntable shows the animal and says nothing
       about it; a chooser that cannot tell you what you picked is a colour swatch. */
    const id = mk('div', 'land-id', this.shark);
    this.nameEl = mk('div', 'land-id-name', id);
    this.latinEl = mk('div', 'land-id-latin', id);

    /* SIZE READS IN METRES, not per cent. "112%" is a number about a slider; "5.3 M" is a
       number about an animal, and it is the one that tells you a whale shark is going to be
       three times the length of the mako you were just looking at. */
    this.dial(this.shark, 'SIZE', 60, 140, () => this.spec.size * 100, (v) => {
      this.spec.size = v / 100;
    }, () => `${this.metres().toFixed(1)} M`);

    this.dial(this.shark, 'AGE', 0, 100, () => this.spec.age * 100, (v) => {
      this.spec.age = v / 100;
    }, () => (this.spec.age < 0.33 ? 'JUVENILE' : this.spec.age < 0.7 ? 'MATURE' : 'OLD'));

    const nameRow = mk('div', 'land-name', this.shark);
    this.nameInput = mk('input', 'land-name-input', nameRow);
    this.nameInput.type = 'text';
    this.nameInput.placeholder = 'NAME YOUR SHARK';
    this.nameInput.maxLength = NAME_MAX;
    this.nameInput.autocomplete = 'off';
    this.nameInput.spellcheck = false;
    this.nameInput.value = this.spec.name;
    // The name is not part of the mesh, so typing must never trigger a rebuild.
    this.nameInput.addEventListener('input', () => {
      this.spec.name = this.nameInput.value;
    });

    const reroll = mk('button', 'land-reroll', nameRow);
    reroll.type = 'button';
    reroll.textContent = 'RESEED';
    reroll.title = 'a different individual of the same species';
    reroll.addEventListener('click', () => {
      this.spec.seed = (Math.random() * 0xffffffff) >>> 0;
      this.queueRebuild();
    });

    /* Say what the empty part of the screen does. A viewport with no affordance is a
       background: nobody drags a background. */
    mk('div', 'land-orbit-hint', this.shark).textContent = coarse()
      ? 'DRAG TO TURN · PINCH TO ZOOM'
      : 'DRAG TO TURN · SCROLL TO ZOOM · RIGHT-DRAG TO PAN';

    this.button(this.shark, 'THIS IS MY SHARK', 'is-primary', () => {
      // A shark nobody named is nobody's shark, but nobody should be BLOCKED on naming one.
      if (this.nameInput.value.trim() === '') this.nameInput.value = 'NAMELESS';
      this.spec.name = this.nameInput.value.trim().slice(0, NAME_MAX);
      saveSpec(this.spec);
      this.shark.classList.add('is-done');
      this.session.classList.add('is-shown');
    });

    // ---- step two: where to swim
    this.session = mk('div', 'land-session', this.panel);

    const invited = bayFromUrl();

    /* Arrived on an invite. The bay in the URL becomes the headline action and everything else
       demotes — someone who followed a link came for one specific place. */
    if (invited && this.online) {
      const box = mk('div', 'land-invited', this.session);
      mk('span', 'land-invited-cap', box).textContent = 'YOU WERE INVITED TO';
      mk('span', 'land-invited-code', box).textContent = formatBay(invited);
      this.button(this.session, 'ENTER THIS BAY', 'is-primary',
        () => this.finish(invited, false), true);
      mk('div', 'land-or', this.session).textContent = 'OR';
    }

    this.button(this.session, 'SHOREPLAY SOLO', invited && this.online ? '' : 'is-primary',
      () => this.finish('', false));

    /* JOIN OPENS A PANEL, it does not teleport you.
       It used to drop you straight into the busiest listed bay, which is wrong twice over: it
       takes away the choice the player came here to make, and it leaves anyone holding a code
       with nowhere to type it. So the button expands, and the panel offers both — the bays that
       are actually open, and a field for a code somebody sent you. */
    const joinBtn = this.button(this.session, 'JOIN PUBLIC SHOREPLAY BAY', '', () => {
      const show = !this.joinBox.classList.contains('is-shown');
      this.joinBox.classList.toggle('is-shown', show);
      joinBtn.classList.toggle('is-expanded', show);
      if (show) this.codeInput.focus();
    }, true);

    this.liveEl = mk('span', 'land-live', joinBtn);

    this.joinBox = mk('div', 'land-join', this.session);
    this.bayListEl = mk('div', 'land-baylist', this.joinBox);
    // Drawn once up front so the panel has its empty state from the start. The first poll takes
    // a round trip, and opening JOIN inside that window would otherwise show a blank gap where
    // the list belongs, which reads as a broken panel rather than a quiet one.
    this.renderBays();
    mk('div', 'land-join-cap', this.joinBox).textContent = 'OR ENTER A CODE';

    this.joinRow = mk('div', 'land-code', this.joinBox);
    this.codeInput = mk('input', 'land-code-input', this.joinRow);
    this.codeInput.type = 'text';
    this.codeInput.placeholder = 'BAY CODE';
    this.codeInput.autocomplete = 'off';
    this.codeInput.spellcheck = false;
    this.codeInput.maxLength = 9;
    const go = mk('button', 'land-code-go', this.joinRow);
    go.type = 'button';
    go.textContent = 'GO';

    const tryCode = (): void => {
      const code = normaliseBay(this.codeInput.value);
      if (code === '') {
        this.codeInput.classList.remove('is-bad');
        // Restart the animation: without the reflow a second bad code does not flash at all.
        void this.codeInput.offsetWidth;
        this.codeInput.classList.add('is-bad');
        return;
      }
      this.finish(code, false);
    };
    go.addEventListener('click', tryCode);
    this.codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') tryCode();
    });
    this.codeInput.addEventListener('input', () => {
      this.codeInput.classList.remove('is-bad');
      const c = normaliseBay(this.codeInput.value);
      if (c) this.codeInput.value = formatBay(c);
    });

    this.button(this.session, 'CREATE PUBLIC SHOREPLAY BAY', '', () => {
      this.finish(makeBayCode(), true);
    }, true);

    if (!this.online) {
      this.panel.classList.add('is-offline');
      mk('div', 'land-foot', this.session).textContent = 'NO RELAY CONFIGURED — SOLO ONLY';
    }
    // No "have a code?" link any more: the code field lives inside JOIN, which is the first
    // place somebody holding a code would look for it.
  }

  /**
   * Redraw the list of open bays.
   *
   * Honest about the empty case rather than hiding the section. "None open" is information; a
   * JOIN panel showing only a text box makes the public half of the feature look broken rather
   * than merely quiet.
   */
  private renderBays(): void {
    this.bayListEl.textContent = '';
    if (this.bays.length === 0) {
      mk('div', 'land-baylist-empty', this.bayListEl).textContent = 'NO PUBLIC BAYS OPEN';
      return;
    }
    for (const b of this.bays) {
      const row = mk('button', 'land-bayrow', this.bayListEl);
      row.type = 'button';
      mk('span', 'land-bayrow-code', row).textContent = formatBay(b.code);
      const free = Math.max(0, b.capacity - b.players);
      mk('span', 'land-bayrow-n', row).textContent =
        `${b.players} SWIMMING · ${free} SPACE${free === 1 ? '' : 'S'}`;
      row.addEventListener('click', () => this.finish(b.code, false));
    }
  }

  /**
   * Where the panel's right edge falls, as a fraction of the viewport.
   *
   * MEASURED off the live element rather than recomputed from the CSS. The panel's width, its
   * padding and its offset are all set in the stylesheet, and a copy of those numbers over here
   * is a copy that goes stale the first time anyone touches the layout — silently, because
   * nothing checks the two agree.
   */
  safeLeftFraction(): number {
    const r = this.panel.getBoundingClientRect();
    const w = window.innerWidth || 1;
    return Math.max(0, Math.min(1, (r.right + 18) / w));
  }

  /** and where its TOP edge falls, for the layout where the panel is a sheet across the bottom */
  safeTopFraction(): number {
    const r = this.panel.getBoundingClientRect();
    const h = window.innerHeight || 1;
    return Math.max(0, Math.min(1, (r.top - 14) / h));
  }

  /** Resolves once the player has chosen. The caller does not start the game before then. */
  choose(): Promise<Session> {
    if (this.online) this.pollBays();
    this.onSpec?.(this.currentSpec());
    return new Promise<Session>((res) => {
      this.resolve = res;
    });
  }

  dispose(): void {
    this.stopPolling();
    if (this.specTimer !== null) window.clearTimeout(this.specTimer);
    this.specTimer = null;
    this.root.remove();
  }

  /**
   * @param needsRelay disable this button when no relay is configured.
   *
   * EXPLICIT, and it has to be. This used to disable everything whose label was not
   * 'SHOREPLAY SOLO', which was true of the only buttons that existed when it was written and
   * became a trap the moment the shark step reused the helper: on a solo-only build — which is
   * every `vite dev` session, because dev deliberately has no default relay — THIS IS MY SHARK
   * was disabled and the landing page could not be got past at all. A rule that infers intent
   * from a label is a rule that silently changes meaning when someone adds a label.
   */
  private button(
    parent: HTMLElement, label: string, cls: string, onClick: () => void, needsRelay = false,
  ): HTMLButtonElement {
    const b = mk('button', `land-btn ${cls}`.trim(), parent);
    b.type = 'button';
    mk('span', 'land-btn-label', b).textContent = label;
    b.addEventListener('click', onClick);
    // Shown and disabled rather than hidden: the point of the landing page is to say what the
    // game is, and multiplayer is part of it.
    if (needsRelay && !this.online) b.disabled = true;
    return b;
  }

  /* Poll the relay for public bays while the landing is up. Live because it is a lobby — a
     count that was true when the page loaded is worse than no count, since it invites someone
     to click JOIN on a bay that emptied a minute ago. */
  private pollBays(): void {
    const tick = async (): Promise<void> => {
      this.abort?.abort();
      this.abort = new AbortController();
      this.bays = await listBays(this.abort.signal);
      const total = this.bays.reduce((n, b) => n + b.players, 0);
      this.liveEl.textContent = this.bays.length === 0
        ? 'NONE OPEN'
        : `${this.bays.length} OPEN · ${total} SWIMMING`;
      this.renderBays();
    };
    void tick();
    this.pollTimer = window.setInterval(() => void tick(), 6000);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.abort?.abort();
    this.abort = null;
  }

  /** a copy, so a caller holding it cannot be surprised by the next slider move */
  private currentSpec(): SharkSpec {
    return { ...this.spec };
  }

  /**
   * A labelled slider that rebuilds the shark.
   *
   * Rebuilds are DEBOUNCED. One costs 300-600 ms on a worker, and a drag emits an input event
   * per pixel: firing on each would queue eighty builds to answer a gesture that had one
   * question in it, and the preview would run a minute behind the dial.
   */
  private dial(
    parent: HTMLElement, label: string, lo: number, hi: number,
    get: () => number, set: (v: number) => void, format: () => string,
  ): void {
    const row = mk('div', 'land-dial', parent);
    mk('span', 'land-dial-name', row).textContent = label;
    const slider = mk('input', 'land-dial-input', row);
    slider.type = 'range';
    slider.min = String(lo);
    slider.max = String(hi);
    slider.step = '1';
    slider.value = String(Math.round(get()));
    const read = mk('span', 'land-dial-read', row);
    const show = (): void => { read.textContent = format(); };
    show();
    // Re-read on any change, not only this slider's: SIZE is shown in metres and the species
    // decides what a metre of it is, so picking a whale shark has to move this number too.
    this.dials.push(show);
    slider.addEventListener('input', () => {
      set(Number(slider.value));
      show();
      this.queueRebuild();
    });
  }

  /** the animal's real length, which is what the size dial is actually setting */
  private metres(): number {
    const sp = speciesList().find((x) => x.key === this.spec.species);
    return (sp ? sp.metres : 4) * this.spec.size;
  }

  /** name, latin and every dial's readout, after anything that changes what is being built */
  private readout(): void {
    const sp = speciesList().find((x) => x.key === this.spec.species);
    this.nameEl.textContent = sp ? sp.name.toUpperCase() : '';
    this.latinEl.textContent = sp ? sp.latin : '';
    for (const d of this.dials) d();
  }

  private markSpecies(): void {
    for (const el of this.speciesRow.children) {
      el.classList.toggle('is-on', (el as HTMLElement).dataset.key === this.spec.species);
    }
  }

  private queueRebuild(): void {
    if (this.specTimer !== null) window.clearTimeout(this.specTimer);
    this.specTimer = window.setTimeout(() => {
      this.specTimer = null;
      this.onSpec?.(this.currentSpec());
    }, 90);
  }

  private finish(bay: string, listed: boolean): void {
    if (this.resolve === null) return;
    this.stopPolling();
    if (this.specTimer !== null) window.clearTimeout(this.specTimer);
    this.specTimer = null;
    saveSpec(this.spec);
    writeBayToUrl(bay);
    this.root.classList.add('is-gone');
    // Let the fade run, then take the screen out of the tree entirely so nothing under it is
    // catching clicks. 420 ms matches the CSS; being late here is harmless, early is not.
    window.setTimeout(() => this.root.remove(), 420);
    const done = this.resolve;
    this.resolve = null;
    done({ bay, listed, spec: this.currentSpec() });
  }
}
